import React from 'react';
import {
  CandidateImageConst,
  CountryCodesConst,
  PositionsConst,
} from '@axiom/const';
import {
  CandidateProposedRateUtil,
  CandidateSubmissionRate,
  CondensedHeader,
  CondensedLabel,
  CondensedMedium,
  Gutter,
  ImageCircle,
  Layout,
  LayoutItem,
  Paragraph,
  Visible,
} from '@axiom/ui';
import type {
  AfcSubmissionCandidateOpportunity,
  AfcSubmission,
  AfcSubmissionPosition,
} from '@axiom/validation';
import { CandidateYearsOfExperienceModel } from '@axiom/ui-models';

import { CandidateUtil } from '../../utils/candidate-util';

const { CountryCodesAbbreviations } = CountryCodesConst;
const { PossibleImageSizes } = CandidateImageConst;
const { ReservedType } = PositionsConst;

export const EngagementTalentTile: React.FC<{
  candidateOpp: AfcSubmissionCandidateOpportunity;
  submission: AfcSubmission;
}> = ({ candidateOpp, submission }) => {
  const position = submission.positions.find(pos =>
    pos.candidateOpportunities.find(co => co.id === candidateOpp.id)
  ) as AfcSubmissionPosition;
  const isInstantMatch = !!position.candidateOpportunities.find(
    co => co.id === candidateOpp.id
  )?.isInstantMatch;

  const getReservedHoursCopy = () => {
    if (position.reservedType !== ReservedType.Reserved) {
      return 'Pay as you go';
    }
    return `${position.billingHoursPerWeek ?? 0} ${position.billingHoursPerWeek === 1 ? 'hour' : 'hours'} reserved`;
  };

  const isEngagementInUS = [null, CountryCodesAbbreviations.US].includes(
    submission.addressCountryCode
  );
  const reservedCopy = isInstantMatch ? (
    <>
      {isEngagementInUS ? <br /> : ''}({getReservedHoursCopy()})
    </>
  ) : (
    ''
  );

  return (
    <Layout horizontalGutter="8px" name="talent-tile">
      <LayoutItem fluid>
        <Layout position="middle" horizontalGutter="8px" wrap>
          <Visible only="largeScreen mobile">
            <LayoutItem>
              <ImageCircle
                name="talent-image"
                size="small"
                imageName={candidateOpp.candidate.calculatedDisplayName}
                src={CandidateUtil.getProfileImageUri(
                  candidateOpp.candidate,
                  PossibleImageSizes.W_100
                )}
              />
            </LayoutItem>
          </Visible>
          <LayoutItem fluid hug>
            <CondensedHeader name="talent-name" maxLines={1}>
              {candidateOpp.candidate.calculatedDisplayName}
            </CondensedHeader>

            <Gutter bottom="8px" />

            <Layout position="middle">
              <div className="max-lines-1">
                <CondensedLabel>Experience</CondensedLabel>
                <Gutter right="8px" />
                <CondensedMedium name="years-of-experience">
                  {`${new CandidateYearsOfExperienceModel(
                    candidateOpp.candidate.yearsOfExperience
                  ).getYearsOfExperienceRange()} years`}
                </CondensedMedium>
              </div>
            </Layout>

            {CandidateProposedRateUtil.shouldDisplayRate(candidateOpp) ? (
              <Layout>
                <Paragraph maxLines={2}>
                  {isEngagementInUS ? (
                    <>
                      <CondensedLabel name="rate-label">Rate</CondensedLabel>
                      <Gutter right="8px" />
                    </>
                  ) : null}
                  <CondensedMedium as="span" name="rate">
                    {isEngagementInUS ? (
                      <CandidateSubmissionRate
                        candidateId={candidateOpp.candidate.id}
                        submission={submission}
                      />
                    ) : null}
                    {reservedCopy}
                  </CondensedMedium>
                </Paragraph>
              </Layout>
            ) : null}
          </LayoutItem>
        </Layout>
      </LayoutItem>
    </Layout>
  );
};
